import { api } from '../api/client';

import { API_ITEMS_URL } from '../utils';
import { normalizeId } from '../utils/ids';
import { readViewerState } from './relatedListState';

/**
 * Related items.
 *
 * Two orthogonal axes describe a relationship:
 *
 * - `origin` — where the edge comes from. `derived` edges are implied by a
 *   card-lookup element value and are never stored; the server recomputes them
 *   on every read, and they can only be removed by clearing the element.
 *   `linked` edges are explicit user-created join documents.
 * - `relationType` — what the edge means. Only `related` exists today;
 *   parent-child / cause-effect are planned, which is why the field is carried
 *   through rather than assumed.
 *
 * The server returns depth-1 edges only and never traverses transitively, so
 * the (inherently cyclic) relationship graph can't recurse.
 */

export const RELATION_ORIGIN = {
	DERIVED: 'derived',
	LINKED: 'linked',
};

export const RELATION_DIRECTION = {
	OUTBOUND: 'outbound',
	INBOUND: 'inbound',
	UNDIRECTED: 'undirected',
};

export const DEFAULT_RELATION_TYPE = 'related';

const relatedUrl = (itemId) => `${API_ITEMS_URL}/${itemId}/related`;

/**
 * Only `linked` edges are removable, so an unrecognized origin is treated as
 * `derived` — a read-only row is a better failure than a remove button that
 * can't work.
 */
const normalizeOrigin = (raw) => (
	raw === RELATION_ORIGIN.LINKED ? RELATION_ORIGIN.LINKED : RELATION_ORIGIN.DERIVED
);

const normalizeRelation = (raw, index) => {
	if (!raw || typeof raw !== 'object') return null;
	const itemId = normalizeId(raw.itemId ?? raw.item_id ?? raw.relatedItemId);
	if (!itemId) return null;

	const origin = normalizeOrigin(raw.origin);
	const relationshipId = origin === RELATION_ORIGIN.LINKED
		? normalizeId(raw._id ?? raw.id ?? raw.relationshipId)
		: undefined;
	const elementName = raw.elementName || raw.element_name || '';
	const direction = raw.direction || (
		origin === RELATION_ORIGIN.LINKED ? RELATION_DIRECTION.UNDIRECTED : RELATION_DIRECTION.OUTBOUND
	);

	// Derived edges have no id of their own, so the key is built from the
	// tuple that makes them unique. The index tail keeps React happy if the
	// server ever repeats a tuple.
	const key = relationshipId
		? `linked:${relationshipId}`
		: `derived:${direction}:${elementName}:${itemId}:${index}`;

	return {
		key,
		relationshipId,
		origin,
		direction,
		itemId,
		relationType: raw.relationType || DEFAULT_RELATION_TYPE,
		elementName,
		elementLabel: raw.elementLabel || raw.element_label || elementName,
		sourceItemId: normalizeId(raw.sourceItemId ?? raw.source_item_id),
		metadata: raw.metadata && typeof raw.metadata === 'object' ? raw.metadata : {},
		createdAt: raw.createdAt || raw.created_at,
		createdBy: raw.createdBy || raw.created_by,
	};
};

/** Accepts the `{ "<id>": item }` map the API sends, or a bare array. */
const normalizeItemMap = (raw) => {
	const map = {};
	const add = (item) => {
		const id = normalizeId(item?._id ?? item?.id);
		if (id) map[id] = item;
	};
	if (Array.isArray(raw)) {
		raw.forEach(add);
	} else if (raw && typeof raw === 'object') {
		Object.entries(raw).forEach(([id, item]) => {
			const normalized = normalizeId(item?._id ?? item?.id) || normalizeId(id);
			if (normalized) map[normalized] = item;
		});
	}
	return map;
};

const normalizeResponse = (data) => {
	const rawList = Array.isArray(data) ? data : (data?.related || []);
	const relations = rawList
		.map((entry, index) => normalizeRelation(entry, index))
		.filter(Boolean);
	return {
		relations,
		itemsById: normalizeItemMap(data?.items),
	};
};

/**
 * Every relationship for an item, both origins merged, plus the far-side item
 * documents so display templates render without a follow-up request.
 *
 * Edges are deliberately NOT de-duplicated across origins: an item that is both
 * referenced by an element and explicitly linked appears twice, once per row.
 */
export async function fetchItemRelationships(itemId, { signal } = {}) {
	const normalizedItemId = normalizeId(itemId);
	if (!normalizedItemId) return { relations: [], itemsById: {} };
	const { data } = await api.get(relatedUrl(normalizedItemId), { signal });
	return normalizeResponse(data);
}

export async function createItemRelationship({
	itemId,
	relatedItemId,
	relationType = DEFAULT_RELATION_TYPE,
	metadata,
}) {
	const normalizedItemId = normalizeId(itemId);
	const normalizedRelatedId = normalizeId(relatedItemId);
	if (!normalizedItemId || !normalizedRelatedId) {
		throw new Error('Both item ids are required to relate items.');
	}
	const payload = { relatedItemId: normalizedRelatedId, relationType };
	if (metadata && typeof metadata === 'object') payload.metadata = metadata;
	const { data } = await api.post(relatedUrl(normalizedItemId), payload);
	return normalizeRelation(data, 0);
}

export async function deleteItemRelationship({ itemId, relationshipId }) {
	const normalizedItemId = normalizeId(itemId);
	const normalizedRelationshipId = normalizeId(relationshipId);
	if (!normalizedItemId || !normalizedRelationshipId) {
		throw new Error('An item id and relationship id are required to unrelate items.');
	}
	const { data } = await api.delete(`${relatedUrl(normalizedItemId)}/${normalizedRelationshipId}`);
	return data;
}

/**
 * The related lists an item's card shows the caller, each with its count under
 * the list's default filter alone (KPR-718): `GET /api/items/{id}/related-lists`.
 *
 *   -> [{ id, cardId, name, count, state, viewerState }]
 *
 * `state` is why a list finds nothing (`X-Related-List-State`), null when it
 * works. `viewerState` is the caller's own saved `{ filter, view }` for the list
 * (KPR-722, `services/relatedListState.js`), or null. A viewer's own filter
 * never moves these numbers; the open list's count
 * is the items list's total. Hidden lists are not here. A 404 (the item is
 * gone, or not the caller's) throws like any other failure; the caller decides.
 */
export async function fetchRelatedLists(itemId, { signal } = {}) {
	const normalizedItemId = normalizeId(itemId);
	if (!normalizedItemId) return [];
	const { data } = await api.get(`${API_ITEMS_URL}/${normalizedItemId}/related-lists`, { signal });
	const lists = Array.isArray(data?.lists) ? data.lists : [];
	return lists
		.map((entry) => {
			const id = typeof entry?.id === 'string' ? entry.id : '';
			if (!id) return null;
			const count = Number(entry.count);
			return {
				id,
				cardId: normalizeId(entry.cardId),
				name: typeof entry.name === 'string' ? entry.name : '',
				count: Number.isFinite(count) && count >= 0 ? count : 0,
				state: typeof entry.state === 'string' && entry.state ? entry.state : null,
				// The caller's own saved filter and grid/table choice (KPR-722).
				viewerState: readViewerState(entry.viewerState),
			};
		})
		.filter(Boolean);
}
